import axios from 'axios';
import { onMounted, reactive, ref } from 'vue';

import { generateUrl } from '@shop/utils/Routing';

import type { FacetChangeEvent, PreselectOption } from './StagedPreselectBlock.types';
import type {
  UseStagedPreselectOptions,
  UseStagedPreselectResult,
} from './useStagedPreselect.types';

export type { UseStagedPreselectResult } from './useStagedPreselect.types';

const getOptionValue = (option: PreselectOption): string | number => {
  if (typeof option === 'object' && option !== null) {
    if ('id' in option) {
      return option.id;
    }
    if ('slug' in option) {
      return option.slug;
    }
  }
  return option as string | number;
};

const getOptionLabel = (option: PreselectOption): string => {
  if (typeof option === 'object' && option !== null) {
    if ('value' in option) {
      return option.value;
    }
    if ('label' in option) {
      return option.label;
    }
  }
  return String(option);
};

export function useStagedPreselect(options: UseStagedPreselectOptions): UseStagedPreselectResult {
  const selectedOptions = reactive<Record<string, string>>({});
  const availableOptions = reactive<Record<string, PreselectOption[]>>({});
  const loading = ref(false);

  if (options.initialOptions) {
    Object.entries(options.initialOptions).forEach(([key, opts]) => {
      if (opts.length > 0) {
        availableOptions[key] = opts;
      }
    });
  }

  const getNextAttribute = (attribute: string): string | undefined => {
    const index = options.attributes.indexOf(attribute) + 1;
    return options.attributes[index];
  };

  const isLastAttribute = (attribute: string): boolean =>
    options.attributes.indexOf(attribute) + 1 === options.attributes.length;

  const resetNextAttributes = (attribute: string): void => {
    const nextIndex = options.attributes.indexOf(attribute) + 1;
    options.attributes.slice(nextIndex).forEach(attr => {
      delete selectedOptions[attr];
      delete availableOptions[attr];
    });
  };

  const stagedActions = {
    async selectFacet(
      event: FacetChangeEvent,
      ignoreNext = false,
      ignoreGetOptions = false
    ): Promise<PreselectOption[] | null> {
      const attribute = event.target.name;
      let { value } = event.target;

      if (options.transformValue) {
        value = await options.transformValue(attribute, value);
      }

      selectedOptions[attribute] = value;
      options.onChange?.(attribute, value);

      if (!ignoreNext) {
        resetNextAttributes(attribute);
      }

      if (isLastAttribute(attribute) && !ignoreNext) {
        loading.value = true;
        await options.onComplete?.();
        return null;
      }

      if (ignoreGetOptions || options.skipGetOptions) {
        loading.value = false;
        return null;
      }

      const nextAttribute = getNextAttribute(attribute);
      if (nextAttribute) {
        return stagedActions.getOptions(nextAttribute, ignoreNext);
      }

      loading.value = false;
      return null;
    },

    async getOptions(attribute: string, ignoreNext = false): Promise<PreselectOption[]> {
      if (!options.optionsRoute) {
        return [];
      }

      loading.value = true;

      try {
        const response = await axios.get<{ options?: PreselectOption[] }>(
          generateUrl(options.optionsRoute, { attribute, ...selectedOptions })
        );
        const opts = response.data.options ?? [];
        availableOptions[attribute] = opts;

        if (opts.length === 1 && !ignoreNext) {
          const option = opts[0];
          const value = String(getOptionValue(option));
          await stagedActions.selectFacet({ target: { name: attribute, value } }, ignoreNext);
        } else {
          loading.value = false;
        }

        return opts;
      } catch {
        loading.value = false;
        return [];
      }
    },
  };

  const { selectFacet, getOptions } = stagedActions;

  const ignorePromiseRejection = (): undefined => undefined;

  const handleChange = (attribute: string, value: string): void => {
    selectFacet({ target: { name: attribute, value } }).catch(ignorePromiseRejection);
  };

  const isFieldDisabled = (_attribute: string, index: number): boolean => {
    if (index === 0) {
      return false;
    }
    const prevAttribute = options.attributes[index - 1];
    return !selectedOptions[prevAttribute];
  };

  const loadFromUrlParams = (params: URLSearchParams, paramMap: Record<string, string>): void => {
    const firstAttr = options.attributes[0];
    const firstValue = params.get(paramMap[firstAttr] ?? firstAttr);
    if (!firstValue) {
      return;
    }

    selectFacet({ target: { name: firstAttr, value: firstValue } }, true)
      .then(async () => {
        await options.attributes.slice(1).reduce<Promise<void>>(async (chain, attr) => {
          await chain;
          const paramKey = paramMap[attr] ?? attr;
          const value = params.get(paramKey) ?? '';
          await selectFacet({ target: { name: attr, value } }, true);
        }, Promise.resolve());
      })
      .catch(ignorePromiseRejection);
  };

  onMounted(() => {
    if (options.initialOptions?.[options.attributes[0]]?.length) {
      return;
    }
    const firstAttr = options.attributes[0];
    if (firstAttr && !availableOptions[firstAttr]?.length) {
      getOptions(firstAttr, true).catch(ignorePromiseRejection);
    }
  });

  return {
    selectedOptions,
    availableOptions,
    loading,
    selectFacet,
    handleChange,
    isFieldDisabled,
    getOptionValue,
    getOptionLabel,
    loadFromUrlParams,
  };
}
